import type { SelectItem } from '@vrplatform/components';

type TeamSwitchPage = {
  list: SelectItem[];
  aggregate: number;
};

const RECENTLY_ACCESSED_GROUP = 'Recently accessed';
const ALL_TEAMS_GROUP = 'All teams';
const OWNER_VR_TRUST_GROUP = 'VR Trust';
const OWNER_VR_PLATFORM_GROUP = 'VR Platform';
const OWNER_OTHER_GROUP = 'Other';
export const MIN_NAVBAR_TEAM_SWITCH_GROUPED_TEAM_COUNT = 4;

const withGroup = <T extends SelectItem>(item: T, group: string): T => ({
  ...item,
  group,
});

const withoutGroup = <T extends SelectItem>(item: T): T => {
  const { group: _group, ...rest } = item;
  return rest as T;
};

const getOwnerProductGroup = (productGroup?: string | null) => {
  if (productGroup === OWNER_VR_TRUST_GROUP) return OWNER_VR_TRUST_GROUP;
  if (productGroup === OWNER_VR_PLATFORM_GROUP) return OWNER_VR_PLATFORM_GROUP;
  return OWNER_OTHER_GROUP;
};

export const shouldGroupNavbarTeamSwitchItems = (totalTeamCount: number) =>
  totalTeamCount >= MIN_NAVBAR_TEAM_SWITCH_GROUPED_TEAM_COUNT;

export const buildNavbarTeamSwitchPinnedItems = ({
  recentTeamIds,
  recentTeams,
  shouldGroupTeams,
}: {
  recentTeamIds: readonly string[];
  recentTeams: readonly SelectItem[];
  shouldGroupTeams: boolean;
}) => {
  if (!shouldGroupTeams) {
    return {
      pinnedItems: [],
      pinnedItemIds: new Set<string>(),
    };
  }

  const recentTeamsById = new Map(
    recentTeams.map((item) => [item.value, item] as const)
  );

  const orderedRecentTeams = recentTeamIds
    .map((teamId) => recentTeamsById.get(teamId))
    .filter((item): item is SelectItem => !!item)
    .map((item) => withGroup(item, RECENTLY_ACCESSED_GROUP));

  return {
    pinnedItems: orderedRecentTeams,
    pinnedItemIds: new Set(orderedRecentTeams.map((item) => item.value)),
  };
};

export const buildNavbarTeamSwitchVisibleData = ({
  allTeamsPrefixItems,
  pages,
  recentTeamIds,
  recentTeams,
  search,
  shouldGroupTeams,
}: {
  allTeamsPrefixItems: readonly SelectItem[];
  pages: readonly TeamSwitchPage[];
  recentTeamIds: readonly string[];
  recentTeams: readonly SelectItem[];
  search: string;
  shouldGroupTeams: boolean;
}) => {
  const recentTeamIdSet = new Set(recentTeamIds);
  const filteredPages = shouldGroupTeams
    ? pages.map((page) => ({
        ...page,
        list: page.list.filter((item) => !recentTeamIdSet.has(item.value)),
      }))
    : pages;

  const { pinnedItems, pinnedItemIds } = buildNavbarTeamSwitchPinnedItems({
    recentTeamIds,
    recentTeams,
    shouldGroupTeams,
  });

  return {
    pinnedItems,
    pages: filterNavbarTeamSwitchPages({
      allTeamsPrefixItems,
      pages: filteredPages,
      pinnedItemIds,
      search,
      showTeamGroups: shouldGroupTeams,
    }),
  };
};

export const resolveNavbarTeamSwitchValue = ({
  dashboard,
  currentTeam,
  hydratedTeamId,
  hydratedTeamName,
  selectedTeamId,
}: {
  dashboard: string | null | undefined;
  currentTeam?: SelectItem | null;
  hydratedTeamId?: string | null;
  hydratedTeamName?: string | null;
  selectedTeamId?: string | null;
}): SelectItem | null => {
  if (dashboard === 'overview') return null;

  if (selectedTeamId && currentTeam?.value === selectedTeamId) {
    return currentTeam;
  }

  if (hydratedTeamId) {
    return {
      label: hydratedTeamName || '',
      value: hydratedTeamId,
    };
  }

  return currentTeam ?? null;
};

export const filterNavbarTeamSwitchPages = ({
  allTeamsPrefixItems,
  pages,
  pinnedItemIds,
  search,
  showTeamGroups,
}: {
  allTeamsPrefixItems: readonly SelectItem[];
  pages: readonly TeamSwitchPage[];
  pinnedItemIds: ReadonlySet<string>;
  search: string;
  showTeamGroups: boolean;
}) => {
  const normalizedSearch = search.trim().toLowerCase();
  const matchesSearch = (item: SelectItem) =>
    !normalizedSearch || item.label.toLowerCase().includes(normalizedSearch);
  const mapTeamItem = (item: SelectItem) =>
    showTeamGroups ? withGroup(item, ALL_TEAMS_GROUP) : item;

  const prefixedAllTeams = allTeamsPrefixItems
    .filter((item) => !pinnedItemIds.has(item.value))
    .filter(matchesSearch)
    .map(mapTeamItem);

  const groupedPages = pages.map((page) => ({
    ...page,
    list: page.list
      .filter((item) => !pinnedItemIds.has(item.value))
      .map(mapTeamItem),
  }));

  if (!prefixedAllTeams.length) return groupedPages;

  const [firstPage, ...remainingPages] = groupedPages;

  if (!firstPage) {
    return [
      {
        aggregate: prefixedAllTeams.length,
        list: prefixedAllTeams,
      },
    ];
  }

  return [
    {
      ...firstPage,
      list: [...prefixedAllTeams, ...firstPage.list],
    },
    ...remainingPages,
  ];
};

export const groupNavbarOwnerSwitchItems = <
  T extends SelectItem & { productGroup?: string | null },
>(
  items: readonly T[]
) => {
  const groupedItems = [
    OWNER_VR_TRUST_GROUP,
    OWNER_VR_PLATFORM_GROUP,
    OWNER_OTHER_GROUP,
  ].flatMap((group) =>
    items
      .filter((item) => getOwnerProductGroup(item.productGroup) === group)
      .map((item) => withGroup(item, group))
  );

  const visibleGroups = new Set(groupedItems.map((item) => item.group));

  if (visibleGroups.size < 2) {
    return items.map(withoutGroup);
  }

  return groupedItems;
};
